import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";

const AuthContext = createContext(null);
const TOKEN_KEY = "dsvm_portal_token";
const USER_KEY = "dsvm_portal_user";

export function PortalAuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = checking
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || null);

  useEffect(() => {
    if (!token) {
      setUser(null);
      return;
    }
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        setUser(r.data);
        localStorage.setItem(USER_KEY, JSON.stringify(r.data));
      })
      .catch((err) => {
        // Only log out on a real server rejection (401/403). On network errors
        // (offline on site) keep the session using the cached user snapshot.
        if (err.response) {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
          setToken(null);
          setUser(null);
          return;
        }
        const cached = localStorage.getItem(USER_KEY);
        if (cached) {
          try { setUser(JSON.parse(cached)); return; } catch { /* fallthrough */ }
        }
        setUser(null);
      });
  }, [token]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
  }, []);

  // Stable axios instance for the lifetime of the provider — interceptor pulls
  // the current token from localStorage so we don't rebuild on every render.
  const apiRef = useRef(null);
  if (apiRef.current === null) {
    const inst = axios.create({ baseURL: API });
    inst.interceptors.request.use((cfg) => {
      const t = localStorage.getItem(TOKEN_KEY);
      if (t) cfg.headers.Authorization = `Bearer ${t}`;
      return cfg;
    });
    apiRef.current = inst;
  }

  const value = useMemo(
    () => ({ user, token, login, logout, api: apiRef.current }),
    [user, token, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const usePortalAuth = () => useContext(AuthContext);
